import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  setDoc,
  updateDoc,
  type CollectionReference,
  type DocumentData,
} from "firebase/firestore";

import { db } from "@/apis/config/firebase";
import {
  DEFAULT_SOFTWARE_TYPES,
  type CreateSoftwareTypeInput,
  type ISoftwareType,
  type UpdateSoftwareTypeInput,
} from "@/features/servers/schemas/servers";

export const SOFTWARE_TYPES_COLLECTION = "software_types";

function getCollection(): CollectionReference<DocumentData> {
  return collection(db, SOFTWARE_TYPES_COLLECTION);
}

function mapDoc(docId: string, data: DocumentData): ISoftwareType {
  return {
    id: docId,
    key: typeof data.key === "string" ? data.key : docId,
    displayName:
      typeof data.displayName === "string" ? data.displayName : data.key ?? docId,
    active: data.active !== false,
    order: typeof data.order === "number" ? data.order : 0,
  };
}

/**
 * Lista tipos de software del catálogo, ordenados.
 */
export async function listSoftwareTypes(): Promise<ISoftwareType[]> {
  const snapshot = await getDocs(getCollection());
  return snapshot.docs
    .map((d) => mapDoc(d.id, d.data()))
    .sort((a, b) => a.order - b.order || a.displayName.localeCompare(b.displayName, "es"));
}

/**
 * Si la colección está vacía, inserta el seed por defecto (doc id = key).
 */
export async function ensureSoftwareTypesSeeded(): Promise<ISoftwareType[]> {
  const existing = await listSoftwareTypes();
  if (existing.length > 0) return existing;

  await Promise.all(
    DEFAULT_SOFTWARE_TYPES.map((item) =>
      setDoc(doc(db, SOFTWARE_TYPES_COLLECTION, item.key), {
        key: item.key,
        displayName: item.displayName,
        active: item.active,
        order: item.order,
      })
    )
  );

  return listSoftwareTypes();
}

export async function createSoftwareType(
  input: CreateSoftwareTypeInput
): Promise<ISoftwareType> {
  const key = input.key.trim().toLowerCase().replace(/\s+/g, "_");
  const payload = {
    key,
    displayName: input.displayName.trim(),
    active: input.active ?? true,
    order: input.order ?? 0,
  };

  const ref = await addDoc(getCollection(), payload);
  return { id: ref.id, ...payload };
}

export async function updateSoftwareType(
  id: string,
  input: UpdateSoftwareTypeInput
): Promise<void> {
  const payload: Record<string, unknown> = {};
  if (input.key !== undefined) {
    payload.key = input.key.trim().toLowerCase().replace(/\s+/g, "_");
  }
  if (input.displayName !== undefined) payload.displayName = input.displayName.trim();
  if (input.active !== undefined) payload.active = input.active;
  if (input.order !== undefined) payload.order = input.order;

  await updateDoc(doc(db, SOFTWARE_TYPES_COLLECTION, id), payload);
}

export async function deleteSoftwareType(id: string): Promise<void> {
  await deleteDoc(doc(db, SOFTWARE_TYPES_COLLECTION, id));
}
